import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import PortalShell, { PortalPanel, PORTAL } from '../../components/public/PortalShell';

const TOKEN_KEY = 'agriware-pay-token';

function usePaymentToken() {
  const { token: urlToken } = useParams();
  const [token] = useState(() => {
    if (urlToken) {
      try { sessionStorage.setItem(TOKEN_KEY, urlToken); } catch {}
      return urlToken;
    }
    try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
  });
  useEffect(() => {
    if (urlToken) window.history.replaceState(null, '', '/pay');
  }, [urlToken]);
  return token;
}

async function post(path, body) {
  const res = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  return { ok: res.ok, body: await res.json().catch(() => ({})) };
}

const STATE_TITLES = {
  invalid: 'Link not valid',
  voided: 'Contract withdrawn',
  not_signed: 'Not ready for payment',
  paid: 'Paid in full',
  in_progress: 'Payment in progress',
  instalment_failed: 'Payment needed',
  error: 'Something went wrong',
};

export default function Pay() {
  const { pathname } = useLocation();
  const complete = pathname === '/pay/complete';
  const token = usePaymentToken();
  const [info, setInfo] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (complete) return;
    if (!token) { setInfo({ state: 'invalid', message: 'This payment link is not valid.' }); return; }
    post('/api/pay/request', { token })
      .then(r => setInfo(r.body.state ? r.body : { state: 'error', message: 'Something went wrong. Please try again.' }))
      .catch(() => setInfo({ state: 'error', message: 'Could not reach the server. Check your connection and try again.' }));
  }, [token, complete]);

  const start = async () => {
    setStarting(true); setError(null);
    try {
      const r = await post('/api/pay/session', { token });
      if (!r.ok || !r.body.url) throw new Error(r.body.message || 'Payment could not be started. Please try again.');
      window.location.href = r.body.url;
    } catch (e) {
      setError(e.message);
      setStarting(false);
    }
  };

  const tone = complete || info?.state === 'paid' ? 'success'
    : ['invalid', 'voided', 'error'].includes(info?.state) ? 'danger' : 'neutral';

  return (
    <PortalShell maxWidth={620} tone={tone} footer="Agriware Ltd · Payments are processed securely by Stripe">
        <PortalPanel>
          {complete ? (
            <>
              <div style={{ fontSize: 20, marginBottom: 10 }}>Thank you</div>
              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                Your payment has been submitted. Card payments confirm straight away. Bank payments can take a few working days, and we will be in touch if anything goes wrong.
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginTop: 14 }}>You can close this page.</div>
            </>
          ) : !info ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
          ) : (
            <>
              {info.title && <>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{info.clientName}</div>
                <div style={{ fontSize: 22, margin: '4px 0 12px' }}>{info.title}</div>
              </>}
              {info.state !== 'payable' && (
                <>
                  <div style={{ fontSize: 17, marginBottom: 8 }}>{STATE_TITLES[info.state] || STATE_TITLES.error}</div>
                  <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{info.message}</div>
                </>
              )}
              {info.terms && (
                <div style={{ marginTop: 14, padding: '12px 14px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                  <div style={{ fontSize: 13, marginBottom: 8 }}>{info.terms}</div>
                  {info.plan === 'instalments_3' && info.instalments.map(i => (
                    <div key={i.n} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: 'var(--color-text-secondary)' }}>
                      <span>Instalment {i.n}{i.n === 1 ? ' (today)' : i.n === 2 ? ' (in 1 month)' : ' (in 2 months)'}</span>
                      <span>{i.amount}{i.status === 'paid' ? ' · Paid' : i.status === 'failed' ? ' · Failed' : ''}</span>
                    </div>
                  ))}
                </div>
              )}
              {info.state === 'payable' && (
                <>
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', margin: '14px 0', lineHeight: 1.6 }}>
                    {info.plan === 'instalments_3'
                      ? 'You pay the first instalment now. The next two are collected automatically from the same card or bank account, one and two months from today.'
                      : 'Pay by card or bank payment. Card payments can be made in your local currency.'}
                    {' '}Payments are processed securely by Stripe.
                  </div>
                  {error && <div style={{ padding: '10px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
                  <button type="button" onClick={start} disabled={starting} style={{
                    width: '100%', height: 46, border: 'none', borderRadius: 'var(--radius-md)', fontSize: 15,
                    background: PORTAL.orange, color: '#fff', cursor: starting ? 'wait' : 'pointer', opacity: starting ? 0.6 : 1,
                  }}>{starting ? 'Opening secure payment...' : 'Continue to secure payment'}</button>
                </>
              )}
              {info.state === 'instalment_failed' && info.failedInvoiceUrl && (
                <a href={info.failedInvoiceUrl} style={{ display: 'block', marginTop: 16, textAlign: 'center', lineHeight: '46px', height: 46, borderRadius: 'var(--radius-md)', fontSize: 15, background: PORTAL.orange, color: '#fff', textDecoration: 'none' }}>
                  Pay outstanding instalment
                </a>
              )}
            </>
          )}
        </PortalPanel>
    </PortalShell>
  );
}
